import { inject } from '@angular/core';
import { CreateWorkListsRequestLocalStorage } from '@model/laboratory/worklist.model';
import { Observable, of } from 'rxjs';
import { WorklistsStorageGateway } from '@service/laboratory/worklist/worklists-storage.gateway';

/**
 * Worklists Storage Use Case
 *
 * Creates work lists from selected specimens.
 * Validates LG codes before delegating to repository.
 *
 * SECURITY: Uses secure in-memory storage with sessionStorage fallback.
 * - Primary storage in memory for security
 * - SessionStorage as fallback for tab persistence during navigation
 * - Automatic TTL expiration (30 minutes)
 * - Cleared on logout
 */
export class WorklistsLocalStorageUsecase {
  private readonly storageGateway = inject(WorklistsStorageGateway);

  /**
   * Initialize and migrate any existing localStorage data
   * Call this on app initialization
   */
  initializeAndMigrate(): void {
    this.storageGateway.migrateFromLocalStorageAndClear();
  }

  created(request: CreateWorkListsRequestLocalStorage): Observable<boolean> {
    try {
      this.storageGateway.set(request);
      return of(true);
    } catch (error) {
      console.error('Failed to save worklists to memory:', error);
      throw new Error('Error saving worklists to memory');
    }
  }

  /**
   * Filtra y elimina los elementos especificados del almacenamiento
   * @param lgList - Array de códigos LG que se deben eliminar
   * @returns Observable<boolean> - true si la operación fue exitosa
   */
  deleteByLgList(lgList: string[]): Observable<boolean> {
    try {
      this.storageGateway.deleteByLgList(lgList);
      return of(true);
    } catch (error) {
      console.error('Failed to filter and delete from memory:', error);
      throw new Error('Error filtering worklists from memory');
    }
  }

  delete(): Observable<boolean> {
    try {
      this.storageGateway.clearAll();
      return of(true);
    } catch (error) {
      console.error('Failed to remove worklists from memory:', error);
      throw new Error('Error removing worklists from memory');
    }
  }

  worklistsExists(): boolean {
    return this.storageGateway.exists();
  }

  /**
   * Gets all worklists from memory
   * @returns CreateWorkListsRequestLocalStorage or null if not found
   */
  getWorklists(): CreateWorkListsRequestLocalStorage | null {
    try {
      return this.storageGateway.get();
    } catch (error) {
      console.error('Failed to get worklists from memory:', error);
      return null;
    }
  }
}
